import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Button, Message, Messages, Modal, useAlert } from "capitalrx-components-2.0/lib/general";
import { Semibold, Body, Heading, Subtitle } from "capitalrx-components-2.0/lib/typography";
import {
  Checkbox,
  Datebox,
  DateSerializer,
  Dropdown,
  Form,
  SubmitButton,
  TextArea,
  Textbox,
  UseFormReturn,
} from "capitalrx-components-2.0/lib/forms";

import { validatePACaseAPI, PACase, getQuestionnairesAPI, ProviderManagement, getProviderManagementAPI } from "@/api";
import { ValidateResultType } from "@/api/interfaces/PACase";
import { DispensingSiteDisplay, DAW_CODES } from "@/constants";
import { DetailsList, DiagnosisCodeMultiSelect, PACaseCreateForm } from "@/components";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton, getThemeURL, numberValidator, npiValidator, validatePhoneNumber, formatPhoneWithExtension } from "@/utils";

import styles from "./PACreateForm.module.scss";
import { NDCDropdown } from "../Medical/BenefitSearchForm/NDCDropdown";


interface ValidateResult {
  result: string;
  reject_codes: string[];
  pa_needed: boolean;
  show_dispensing_site: boolean;
}

interface FormProps {
  goToNextStep: Function;
  form: UseFormReturn<PACaseCreateForm>;
  resetQuestionnaires: Function;
}


export const CaseTestClaimForm: FC<FormProps> = ({ goToNextStep, form, resetQuestionnaires }) => {
  const navigate = useNavigate();
  const showAlert = useAlert();
  const [provider, setProvider] = useState<ProviderManagement.ProviderSchema>();
  const getProvider = useCallback(async () => {

    try {
      // Find provider management record and process response
      const authToken = getAuthToken();
      if (!authToken) {
        return;
      }
      const pmResponse = await getProviderManagementAPI(authToken);
      setProvider(pmResponse.provider);

    } catch (error) {
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementGet);
    }
  }, []);

  const { getAuthToken } = useContext(AuthenticationContext);
  useEffect(() => {
    getProvider();
  }, []);

  const { primary_color, primary_hover_color, secondary_hover_color } = useContext(ThemeSelectorContext);

  const [patientError, setPatientError] = useState<string>();
  const [drugError, setDrugError] = useState<string>();
  const [prescriberError, setPrescriberError] = useState<string>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [hasDispensingSite, setHasDispensingSite] = useState<boolean>(false);

  const dispensingSite = form.watch("dispensing_site");
  const alternateFaxNumber = form.watch("alternate_fax_number");

  useEffect(() => {
    if (alternateFaxNumber) {
      const formatted = formatPhoneWithExtension(alternateFaxNumber);
      if (formatted !== alternateFaxNumber) {
        form.setValue("alternate_fax_number", formatted);
      }
    }
  }, [alternateFaxNumber, form]);

  const getValidateResult = () => {

    const formValues = form.getValues();
    let resultDisplay = "";
    if (formValues?.review_type === "non_formulary_exception") {
      resultDisplay = "Non-formulary drug";
    } else if (formValues?.review_type === "step_therapy_exception") {
      resultDisplay = "Step therapy required";
    } else if (formValues?.review_type === "prior_authorization_exception") {
      resultDisplay = "Prior authorization required";
    } else if (formValues?.review_type === "no_review_needed") {
      resultDisplay = "No prior authorization required";
    } else if (formValues?.review_type === "tiering_exception") {
      resultDisplay = "Prior authorization required";
    }

    const showDispensingSite = formValues?.test_claim?.dispensing_sites && formValues?.test_claim?.dispensing_sites.includes("prescriber_administered");
    if (!showDispensingSite && !hasDispensingSite) {
      form.setValue("dispensing_site", "pharmacy");
      form.setValue("facility_npi", null);
      dispensingSiteFormSubmitted();
    }

    return {
      result: resultDisplay,
      reject_codes: formValues?.test_claim?.reject_codes,
      pa_needed: formValues?.review_type !== "no_review_needed",
      show_dispensing_site: showDispensingSite,
    };
  };
  const [editView, setEditView] = useState<boolean>(!form.getValues("test_claim"));
  const [validateResult, setValidateResult] = useState<ValidateResult>({
    result: null,
    reject_codes: [],
    pa_needed: true,
    show_dispensing_site: true,
  });

  const { three_six_twelve_columns, isCompressed } = useResponsive({
    three_six_twelve_columns: {
      desktop: 4,
      tablet: 6,
      mobile: 12,
    },
    isCompressed: {
      desktop: false,
      tablet: false,
      mobile: true,
    },
  });
  const StackOrShelf = isCompressed ? Stack : Shelf;

  const displayError = useCallback((error: string) => {
    showAlert({ type: "error", style: "banner", message: error });
  }, [showAlert]);

  const resetFormState = () => {
    setPatientError("");
    setDrugError("");
    setPrescriberError("");
    setValidateResult({
      result: null,
      reject_codes: [],
      pa_needed: true,
      show_dispensing_site: true,
    });
  };

  const prefillLoggedInAccount = useCallback(async () => {
    if (!provider || !provider.billing_npi) return;
    try {
      form.setValue("npi", provider.billing_npi);
    } catch (error) {
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementGet);
    }
  }, [form, provider]);

  const getQuestionnaires = useCallback(async () => {
    const data = form.getValues();

    // Don't fetch questionnaires if they don't need a PA
    if (!data.review_type || data.review_type === "no_review_needed") {
      return;
    }

    try {
      const questionnaires = await getQuestionnairesAPI(
        {
          drug_ndc: data.ndc,
          claim_compound_code: data.test_claim.compound_code,
          claim_reject_codes: data.test_claim.reject_codes,
          person_code: data.test_claim.person_code,
          external_account_id: data.test_claim.external_account_id,
          external_member_id: data.test_claim.external_member_id,
          member_client_id: data.test_claim.client_id,
          member_group_id: data.test_claim.group_id,
          programs: data.test_claim.programs,
          review_types: [data.review_type],
          dispensing_site: data.dispensing_site,
        },
      );

      // form success
      form.setValue("questionnaires", questionnaires);
    } catch (error) {
      console.error(error);
      showAlert({ type: "error", style: "banner", message: "Failed to load clinical criteria questions" });
    }
  },
    [showAlert, form],
  );

  const formSubmitted = useCallback(
    async (data: Partial<PACaseCreateForm>) => {
      // Remove all errors
      resetFormState();

      // Analytics
      logEvent(ANALYTIC_EVENTS.PA_CASE_CREATE.validate);

      const sanitizedData = {
        external_member_id: data.external_member_id,
        member_first_name: data.member_first_name,
        member_last_name: data.member_last_name,
        member_date_of_birth: data.member_date_of_birth,
        ndc: data.ndc,
        npi: data.npi,
        quantity_dispensed: data.quantity_dispensed,
        days_supply: data.days_supply,
        icd_10: data.icd_10,
        daw_code: data.daw_code,
      };

      try {
        // Send test claim and process response
        const testClaimResult = await validatePACaseAPI(sanitizedData);
        await handleTestClaimResult(testClaimResult);
      } catch (error) {
        console.error(error);
        displayError("Failed to process test claim. Please try again later.");
        // analytics log validate fail
        logEvent(ANALYTIC_EVENTS.ERRORS.caseValidate);
      }
    },
    [],
  );

  const dispensingSiteFormSubmitted = useCallback(
    async () => {
      setHasDispensingSite(true);
      await getQuestionnaires();
      goToNextStep();
    },
    [],
  );

  const handleTestClaimResult = async (testClaimResult: PACase.PACaseValidateResponse) => {
    const result = testClaimResult.result;

    // Error Handling
    if (result === ValidateResultType.PatientNotFound) {
      setPatientError("No patient found - check that all information entered is correct");
      return;
    } else if (result === ValidateResultType.DrugNotFound) {
      setDrugError("No drug found - check that NDC is correct");
      return;
    } else if (result === ValidateResultType.PrescriberNotFound) {
      setPrescriberError("Prescriber not found.");
      return;
    } else if (
      [
        ValidateResultType.PatientSearchFail,
        ValidateResultType.PrescriberSearchFail,
        ValidateResultType.TestClaimFail,
      ].includes(result)
    ) {
      displayError("Failed to process test claim. Please try again later.");
      return;
    }

    // Set Review Type
    if (result === ValidateResultType.Nonformulary) {
      form.setValue("review_type", "non_formulary_exception");
    } else if (result === ValidateResultType.StepTherapyException) {
      form.setValue("review_type", "step_therapy_exception");
    } else if (result === ValidateResultType.PARequired) {
      form.setValue("review_type", "prior_authorization_exception");
    } else if (result === ValidateResultType.PANotRequired) {
      form.setValue("review_type", "no_review_needed");
    }

    // form success
    form.setValue("test_claim", testClaimResult.test_claim);

    // Set the validate result so we can display it nicely
    setValidateResult(getValidateResult());
    setEditView(false);
  };

  const goToPAHome = useCallback(() => {
    // log cancel click
    logEvent(ANALYTIC_EVENTS.PA_CASE_CREATE.cancel);
    navigate(getThemeURL("PA_SEARCH"));
  }, [navigate]);

  const editModal = (
    <Modal.Approve
      visible={showEditModal}
      onClose={(approved: boolean) => {
        if (approved) {
          // remove result of this form so it sets back to this step
          form.setValue("test_claim", null);
          resetQuestionnaires();
          setHasDispensingSite(false);
          setValidateResult(getValidateResult());
          setEditView(true);
          goToNextStep();
        }
        setShowEditModal(false);
      }}
      title="Edit Case Details"
      denyContent="Cancel"
      approveContent="Continue"
    >
      <Body>
        Editing case details will require a new test claim.
        Any information entered after the test claim result will be lost.
      </Body>
    </Modal.Approve>
  );

  const getDetails = () => {
    const data = form.getValues();

    let drugName = "";
    if (data.test_claim?.drug_name) {
      drugName = data.test_claim.drug_name;
      if (data.test_claim?.drug_dosage_form) {
        drugName += `, ${data.test_claim.drug_dosage_form}`;
      }
      if (data.test_claim?.drug_dosage) {
        drugName += ` (${data.test_claim.drug_dosage})`;
      }
      drugName += `, Quantity: ${data.quantity_dispensed}, Days Supply: ${data.days_supply}`;
    }

    let prescriberName = "";
    if (data.test_claim?.prescriber_first && data.test_claim?.prescriber_last) {
      prescriberName = `${data.test_claim?.prescriber_first} ${data.test_claim?.prescriber_last}`;
    }

    let displayValues = {
      "Prescriber NPI": data.npi,
      "Alternate Fax Number": data?.alternate_fax_number ?? provider?.authorized_official_fax ?? "N/A",
      "Urgent": data.is_urgent ? "Yes" : "No",
      "Member ID": data.external_member_id,
    };

    if (prescriberName) {
      displayValues = { ...{ "Prescriber": prescriberName }, ...displayValues };
    }

    if (drugName) {
      displayValues["Drug"] = drugName;
    }
    displayValues["NDC"] = data.ndc;
    displayValues["DAW Code"] = DAW_CODES[data.daw_code];

    if (hasDispensingSite) {
      displayValues["Dispensing Site"] = DispensingSiteDisplay[data.dispensing_site];
      if (data.dispensing_site === "prescriber_administered") {
        displayValues["Dispensing Provider NPI"] = data.facility_npi;
      }
    }

    return displayValues;
  };

  useEffect(() => {
    prefillLoggedInAccount();
  }, [form, provider]);

  return (
    <Stack>
      {editModal}
      {editView
        && <>
          <Form form={form} onSubmit={formSubmitted}>
            <Stack spacing="large">
              <div className={styles.PACreateFormInstructionsPanel}>
                <Stack spacing="none">
                  <Semibold>Instructions</Semibold>
                  <ul>
                    <li>Enter case, member, and drug information to run a test claim and verify that a prior authorization is required</li>
                    <li><Semibold>To speed up the decision process and prevent unnecessary denials,</Semibold> complete the resulting clinical criteria questions in full</li>
                    <li>After submitting the prior authorization, upload any documentation that is important for the review (e.g. chart notes, lab data)</li>
                  </ul>
                </Stack>
              </div>

              <Stack>
                <Heading>Case</Heading>
                {prescriberError && <Message appearance={Messages.Error} visible>{prescriberError}</Message>}
                <Grid>
                  <Grid.Span columns={three_six_twelve_columns}>
                    <Textbox.Field testId="test__npi" label="Prescriber NPI" name="npi" validator={npiValidator} required />
                  </Grid.Span>
                  <Grid.Break />
                  <Grid.Span columns={three_six_twelve_columns}>
                    <Textbox.Field
                      testId="test__alternate_fax_number"
                      label="Alternate Fax Number"
                      name="alternate_fax_number"
                      validator={validatePhoneNumber}
                      instructions="123-456-7890 x 123"
                    />
                  </Grid.Span>
                </Grid>
                <Checkbox.Field
                  className={styles.PACreateFormCheckboxNoLabel}
                  name="is_urgent"
                  label="Urgent - The patient is facing loss of life or life-altering loss of ability within 30 days"
                  testId="test__is_urgent_checkbox"
                />
              </Stack>

              <Stack>
                <Heading>Patient</Heading>
                {patientError && <Message appearance={Messages.Error} visible>{patientError}</Message>}
                <Grid>
                  <Grid.Span columns={three_six_twelve_columns}>
                    <Textbox.Field testId="test__member_id" label="Member ID" name="external_member_id" required />
                  </Grid.Span>
                  <Grid.Break />
                  <Grid.Span columns={three_six_twelve_columns}>
                    <Textbox.Field testId="test__first_name" label="First Name" name="member_first_name" required />
                  </Grid.Span>
                  <Grid.Span columns={three_six_twelve_columns}>
                    <Textbox.Field testId="test__last_name" label="Last Name" name="member_last_name" required />
                  </Grid.Span>
                  <Grid.Span columns={three_six_twelve_columns}>
                    <Datebox.Field zone="local" serializer={DateSerializer.Literal} testId="test__date_of_birth" label="Date of Birth" name="member_date_of_birth" instructions="mm/dd/yyyy" required />
                  </Grid.Span>

                </Grid>
              </Stack>

              <Stack>
                <Heading>Drug</Heading>
                {drugError && <Message appearance={Messages.Error} visible>{drugError}</Message>}
                <Grid>
                  <Grid.Span columns={three_six_twelve_columns}>
                    <div data-testid="test__ndc">
                      <Stack spacing="mini">
                        <NDCDropdown required />
                        <Body size="mini" style={{ color: "#3E5D6F" }}>
                          Search by drug name, NDC/GPI, or exact match for HCPCS code
                        </Body>
                      </Stack>
                    </div>
                  </Grid.Span>
                  <Grid.Span columns={three_six_twelve_columns}>
                    <Textbox.Field testId="test__quantity" label="Quantity (Units)" name="quantity_dispensed" instructions="Enter the quantity or units to be dispensed" validator={numberValidator} required />
                  </Grid.Span>
                  <Grid.Span columns={three_six_twelve_columns}>
                    <Textbox.Field testId="test__days_supply" label="Days Supply" name="days_supply" validator={numberValidator} required />
                  </Grid.Span>
                  <Grid.Span columns={12}>
                    <TextArea.Field testId="test__directions_for_use" label="Directions For Use" name="directions_for_use" required />
                  </Grid.Span>
                  <Grid.Span columns={three_six_twelve_columns}>
                    <Dropdown.Field testId="test__daw" name="daw_code" label="DAW Code" required>
                      {Object.keys(DAW_CODES).map((key) => (
                        <Dropdown.Option key={key} value={key}>{DAW_CODES[key]} </Dropdown.Option>
                      ))}
                    </Dropdown.Field>
                  </Grid.Span>
                </Grid>
              </Stack>

              <Stack>
                <Heading>Diagnosis Codes</Heading>
                <DiagnosisCodeMultiSelect name="icd_10" required />
              </Stack>

              <StackOrShelf spacing="small">
                <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)} testId="test__create_button">Run Test Claim</SubmitButton>
                {!form.formState.isSubmitting && (
                  <Button appearance={getThemeButton("secondary", primary_color, secondary_hover_color)} onClick={goToPAHome}>
                    Cancel
                  </Button>
                )}
                <Shelf.Spacer />
              </StackOrShelf>
            </Stack>
          </Form>
        </>}

      {!editView
        && <Stack spacing="large">
          <Stack>
            <StackOrShelf>
              <Subtitle>Case</Subtitle>
              <Shelf.Spacer />
              <Button appearance="secondary" icon="edit" iconPosition="left" onClick={() => setShowEditModal(true)}>Edit</Button>
            </StackOrShelf>

            <DetailsList data={getDetails()} />
          </Stack>

          <div className={`${styles.PACreateFormTestClaimResult} ${validateResult.pa_needed ? "" : styles.NoReview}`} style={{ "--primary-color": primary_color } as React.CSSProperties}>
            <Heading className={`${styles.PACreateFormTestClaimResultHeading} ${validateResult.pa_needed ? "" : styles.NoReview}`} style={{ "--primary-color": primary_color } as React.CSSProperties}>
              Test Claim Result
            </Heading>
            <Body className={styles.PACreateFormTestClaimResultBody} style={{ "--primary-color": primary_color } as React.CSSProperties}>
              {validateResult.result
                && <><Semibold>{validateResult.result}</Semibold><br /></>
              }
              {validateResult.reject_codes && validateResult.pa_needed
                && <span>Reject code(s): {validateResult.reject_codes.join(", ")}</span>
              }
              {!validateResult.pa_needed
                && <span>Test claim accepted</span>
              }
            </Body>
          </div>
          {!validateResult.pa_needed
            && <StackOrShelf>
              <Button appearance="secondary" onClick={goToPAHome}>Cancel</Button>
              <Shelf.Spacer />
            </StackOrShelf>
          }
          {validateResult.pa_needed && validateResult.show_dispensing_site && !hasDispensingSite
            && <Form form={form} onSubmit={dispensingSiteFormSubmitted}>
              <Stack>
                <Grid>
                  <Grid.Span columns={three_six_twelve_columns}>
                    <Dropdown.Field testId="test__dispensing_site" label="Dispensing Site" name="dispensing_site" required>
                      {Object.keys(DispensingSiteDisplay).map((key) => (
                        <Dropdown.Option key={key} value={key}>{DispensingSiteDisplay[key]}</Dropdown.Option>
                      ))}
                    </Dropdown.Field>
                  </Grid.Span>
                  {dispensingSite === "prescriber_administered"
                    && <Grid.Span columns={three_six_twelve_columns}>
                      <Textbox.Field
                        testId="test__facility_npi"
                        label="Dispensing Provider NPI"
                        name="facility_npi"
                        validator={npiValidator}
                        required
                      />
                    </Grid.Span>
                  }
                </Grid>
                <StackOrShelf spacing="small">
                  <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)} testId="test__continue_button">Continue</SubmitButton>
                  {!form.formState.isSubmitting && (
                    <Button appearance={getThemeButton("secondary", primary_color, secondary_hover_color)} onClick={goToPAHome}>
                      Cancel
                    </Button>
                  )}
                  <Shelf.Spacer />
                </StackOrShelf>
              </Stack>
            </Form>
          }
        </Stack>}
    </Stack>
  );
};

export default CaseTestClaimForm;
